import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Button,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import SortableQuestion from "./SortableQuestion";
import QuestionModal from "./QuestionModal";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "../../components/SvgIcons/SvgIcons";
import { Helmet } from "react-helmet-async";
import { APP_TITLE } from "../../constant";
import { AxiosService } from "../../services/axios/axiosService";
import toast from "react-hot-toast";

// Allowed HTML tags for job description (safe subset for formatting only)
const JD_ALLOWED_TAGS = ["p", "br", "ul", "ol", "li", "strong", "b", "em", "i", "u", "h1", "h2", "h3", "span", "div"];

/** Sanitize HTML for safe rendering; keeps only formatting tags. */
const sanitizeJobDescriptionHtml = (html) => {
  if (!html || typeof html !== "string") return "";
  if (typeof document === "undefined") return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

  const div = document.createElement("div");
  div.innerHTML = html;

  const voidTags = ["br"];
  const collectSafe = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || "";
      return text.replace(/\n/g, "<br />");
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return "";

    const tag = node.tagName?.toLowerCase();
    if (!tag || !JD_ALLOWED_TAGS.includes(tag)) {
      return Array.from(node.childNodes).map(collectSafe).join("");
    }
    if (voidTags.includes(tag)) return tag === "br" ? "<br />" : "";
    const inner = Array.from(node.childNodes).map(collectSafe).join("");
    return `<${tag}>${inner}</${tag}>`;
  };

  return Array.from(div.childNodes).map(collectSafe).join("");
};

/** Whether the string looks like HTML. */
const looksLikeHtml = (str) => typeof str === "string" && /<[a-z][\s\S]*>/i.test(str);

/** Get plain text from HTML or raw string; preserves newlines. */
function getPlainText(raw) {
  if (!raw || typeof raw !== "string") return "";
  if (looksLikeHtml(raw)) {
    if (typeof document === "undefined") return raw.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    const div = document.createElement("div");
    div.innerHTML = raw.replace(/<br\s*\/?>/gi, "\n").replace(/<\/p>|<\/div>|<\/li>/gi, "\n");
    const text = (div.textContent || div.innerText || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    return text.replace(/[ \t]+/g, " ").replace(/\n\s+/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
  }
  return raw.replace(/\r\n/g, "\n").replace(/\r/g, "\n").trim();
}

/** Match "Responsibilities" or "Key Responsibilities" (inline or after newline). */
const RESPONSIBILITIES_REGEX = /\b(Key\s+Responsibilities|Responsibilities)\s*:?\s*/i;

/** Match "Requirements" / "Qualifications" etc. (inline or after newline). */
const REQUIREMENTS_WITH_NEWLINE = /(\n\s*)(Requirements|Qualifications|Key Requirements|Required Qualifications|Must Have|Skills Required|Experience Required)\s*:?\s*(\n|$)/gi;
const REQUIREMENTS_INLINE = /\b(Requirements|Qualifications|Key Requirements|Required Qualifications|Must Have|Skills Required|Experience Required)\s*:?\s*/i;

/** Find first match of regex in string; returns [index, fullMatchLength] or null. */
function findSectionMatch(str, regex) {
  const m = str.match(regex);
  return m ? [m.index, m[0].length] : null;
}

/** Split JD into Job Description, Responsibilities, and Requirements sections. */
function splitJobDescriptionSections(plainText) {
  if (!plainText || !plainText.trim()) return { description: "", responsibilities: "", requirements: "" };
  const normalized = plainText.trim();

  const respMatch = findSectionMatch(normalized, RESPONSIBILITIES_REGEX);

  let reqMatch = findSectionMatch(normalized, REQUIREMENTS_WITH_NEWLINE);
  REQUIREMENTS_WITH_NEWLINE.lastIndex = 0;
  if (!reqMatch) reqMatch = findSectionMatch(normalized, REQUIREMENTS_INLINE);

  const respIdx = respMatch ? respMatch[0] : -1;
  const respLen = respMatch ? respMatch[1] : 0;
  const reqIdx = reqMatch ? reqMatch[0] : -1;
  const reqLen = reqMatch ? reqMatch[1] : 0;

  if (respIdx < 0 && reqIdx < 0) {
    return { description: normalized, responsibilities: "", requirements: "" };
  }
  if (respIdx < 0) {
    return {
      description: normalized.slice(0, reqIdx).trim(),
      responsibilities: "",
      requirements: normalized.slice(reqIdx + reqLen).trim(),
    };
  }
  if (reqIdx < 0) {
    return {
      description: normalized.slice(0, respIdx).trim(),
      responsibilities: normalized.slice(respIdx + respLen).trim(),
      requirements: "",
    };
  }
  if (respIdx < reqIdx) {
    return {
      description: normalized.slice(0, respIdx).trim(),
      responsibilities: normalized.slice(respIdx + respLen, reqIdx).trim(),
      requirements: normalized.slice(reqIdx + reqLen).trim(),
    };
  }
  return {
    description: normalized.slice(0, reqIdx).trim(),
    responsibilities: "",
    requirements: normalized.slice(reqIdx + reqLen).trim(),
  };
}

/** Ensure sentence ends with . ! or ? for display. */
function ensureSentenceEnd(str) {
  const t = str.trim();
  if (!t) return t;
  if (/[.!?]$/.test(t)) return t;
  return t + ".";
}

/** Turn section text into bullet lines — new line only after full stop (.). */
function textToBulletLines(text) {
  if (!text || !text.trim()) return [];
  const s = text.trim();
  const one = s.replace(/\r\n/g, " ").replace(/\r/g, " ").replace(/\n+/g, " ").replace(/\s+/g, " ").trim();
  if (!one) return [];
  const parts = one.split(/\.\s+/).map((p) => p.trim()).filter(Boolean);
  if (parts.length <= 1) return parts.length ? [ensureSentenceEnd(parts[0])] : [];
  return parts.map((p) => ensureSentenceEnd(p));
}

const InterviewQuestions = () => {
  const theme = useTheme();
  const { id: positionId } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [formData, setFormData] = useState({
    question: "",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [jobDescExpanded, setJobDescExpanded] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const fetchInterviews = async () => {
    try {
      setLoading(true);
      const response = await AxiosService.get(
        `/api/Position/viewQuestions?positionId=${positionId}`,
      );
      setQuestions(response);
    } catch (err) {
      toast.error(
        err.message || "Oops! Something went wrong while retrieving the data.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInterviews();
  }, [positionId]);

  useEffect(() => {
    setJobDescExpanded(false);
  }, [positionId]);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const currentQuestions = questions?.questions;
    if (!Array.isArray(currentQuestions) || currentQuestions.length === 0)
      return;
    const oldIndex = currentQuestions.findIndex(
      (item) => item.id === active.id,
    );
    const newIndex = currentQuestions.findIndex((item) => item.id === over.id);
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }
    const reorderedItems = arrayMove(currentQuestions, oldIndex, newIndex);
    const updatedItems = reorderedItems.map((item, index) => ({
      ...item,
      questionOrder: index + 1,
    }));
    const payload = updatedItems.map(({ id, questionOrder }) => ({
      id,
      questionOrder,
    }));
    const previousState = { ...questions };
    setQuestions((prev) => ({
      ...prev,
      questions: updatedItems,
    }));
    setReordering(true);
    try {
      await AxiosService.put(
        `/api/position/${positionId}/questions/reorder`,
        payload,
      );
    } catch (error) {
      setQuestions(previousState);
      const errorMessage =
        error?.message || "Failed to update question order. Please try again.";
      toast.error(errorMessage);
    } finally {
      setReordering(false);
    }
  };

  const handleOpenModal = (question = null) => {
    if (question) {
      setEditingQuestion(question);
      setFormData({
        question: question.questionText,
      });
    } else {
      setEditingQuestion(null);
      setFormData({
        question: "",
      });
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setTimeout(() => {
      setEditingQuestion(null);
      setFormData({ question: "" });
    }, 300);
  };

  const handleSaveQuestion = async () => {
    try {
      if (!formData?.question?.trim()) {
        toast.error("Question cannot be empty");
        return;
      }
      setSaving(true);
      const payload = {
        questionText: formData.question.trim(),
        positionId,
        ...(editingQuestion && {
          questionId: editingQuestion.id,
          questionOrder: editingQuestion.questionOrder,
          expectedDurationSeconds: editingQuestion.expectedDurationSeconds,
        }),
      };
      const res = await AxiosService.post(
        "/api/position/upsertQuestion",
        payload,
      );
      toast.success(
        res ||
          (editingQuestion
            ? "Question updated successfully"
            : "Question added successfully"),
      );
      await fetchInterviews();
      handleCloseModal();
    } catch (err) {
      toast.error(
        err.message || "Oops! Something went wrong while retrieving the data.",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="93vh"
        sx={{ backgroundColor: theme.palette.background.default }}
      >
        <CircularProgress
          size={40}
          thickness={4}
          sx={{ color: "primary.main" }}
        />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title> IGNA Scout Setup | {APP_TITLE}</title>
        <meta
          name="IGNA Scout Setup"
          content="Review and finalize interview questions for candidates"
        />
      </Helmet>
      <Box
        sx={{
          p: 4,
          backgroundColor: theme.palette.background.default,
          minHeight: "100vh",
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 0.5,
            }}
          >
            <Box
              onClick={() => navigate(-1)}
              sx={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                padding: "8px",
                borderRadius: "8px",
                transition: "all 0.2s",
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "#f3f4f6",
                },
              }}
            >
              <ArrowLeft size={24} color={theme.palette.text.primary} />
            </Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, color: theme.palette.text.primary }}
            >
              IGNA Scout Setup
            </Typography>
          </Box>
         
        </Box>

        {/* Job Information Card */}
        <Card
          elevation={2}
          sx={{
            mb: 3,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0px 1px 3px rgba(0, 0, 0, 0.3)"
                : "0px 1px 3px rgba(0, 0, 0, 0.1)",
            borderRadius: 3,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Typography
              variant="h6"
              fontWeight={600}
              gutterBottom
              sx={{ color: theme.palette.text.primary, mb: 3 }}
            >
              Job Information
            </Typography>

            <Stack direction="row" spacing={10} sx={{ mt: 2 }}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 500,
                    display: "block",
                    mb: 0.5,
                  }}
                >
                  Job Title
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
                >
                  {questions?.title || "-"}
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 500,
                    display: "block",
                    mb: 0.5,
                  }}
                >
                  Job ID
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.primary, fontWeight: 500 }}
                >
                  {questions?.jobOpeningID || "-"}
                </Typography>
              </Box>
              
            </Stack>

            {(() => {
              const raw = questions?.jobDescription;
              const isEmpty = raw == null || String(raw).trim() === "";
              if (isEmpty) {
                return (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      -
                    </Typography>
                  </Box>
                );
              }
              const plainText = getPlainText(raw);
              const { description, responsibilities, requirements } = splitJobDescriptionSections(plainText);
              const descriptionLines = textToBulletLines(description);
              const responsibilitiesLines = textToBulletLines(responsibilities);
              const requirementsLines = textToBulletLines(requirements);
              const isLong = plainText.length > 600;
              const showExpanded = isLong && jobDescExpanded;

              const bulletListSx = {
                margin: 0,
                paddingLeft: 2.5,
                listStyle: "disc",
                "& li": {
                  marginBottom: 0.5,
                  lineHeight: 1.5,
                  fontSize: "0.815rem",
                  color: theme.palette.text.primary,
                },
              };
              const headingSx = {
                fontWeight: 600,
                color: theme.palette.text.primary,
                fontSize: "0.815rem",
                mb: 0.75,
                mt: 2,
                "&:first-of-type": { mt: 0 },
              };

              return (
                <Box sx={{ mt: 2 }}>
                  <Box
                    sx={{
                      ...(showExpanded
                        ? {}
                        : {
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }),
                    }}
                  >
                    <Typography variant="caption"  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 500,
                    display: "block",
                    mb: 0.5,
                  }}>
                      Job Description
                    </Typography>
                    <Typography variant="subtitle2" sx={headingSx}>Role Overview </Typography>
                    {descriptionLines.length > 0 ? (
                      <Box component="ul" sx={bulletListSx}>
                        {descriptionLines.map((line, i) => (
                          <Box component="li" key={`jd-${i}`}>
                            {line}
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="caption" sx={{ color: theme.palette.text.primary, pl: 0 }}>
                        -
                      </Typography>
                    )}

                    {responsibilities.length > 0 && (
                      <>
                        <Typography variant="subtitle2" sx={headingSx}>
                          Key Responsibilities
                        </Typography>
                        {responsibilitiesLines.length > 0 ? (
                          <Box component="ul" sx={bulletListSx}>
                            {responsibilitiesLines.map((line, i) => (
                              <Box component="li" key={`resp-${i}`}>
                                {line}
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="caption" sx={{ color: theme.palette.text.primary, pl: 0 }}>
                            -
                          </Typography>
                        )}
                      </>
                    )}

                    {requirements.length > 0 && (
                      <>
                        <Typography variant="subtitle2" sx={headingSx}>
                          Requirements
                        </Typography>
                        {requirementsLines.length > 0 ? (
                          <Box component="ul" sx={bulletListSx}>
                            {requirementsLines.map((line, i) => (
                              <Box component="li" key={`req-${i}`}>
                                {line}
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="caption" sx={{ color: theme.palette.text.primary, pl: 0 }}>
                            -
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                  {isLong && (
                    <Typography
                      component="button"
                      type="button"
                      variant="caption"
                      onClick={() => setJobDescExpanded((e) => !e)}
                      sx={{
                        color: "primary.main",
                        fontWeight: 600,
                        display: "inline-block",
                        mt: 0.5,
                        cursor: "pointer",
                        border: "none",
                        background: "none",
                        padding: 0,
                        font: "inherit",
                      }}
                    >
                      {jobDescExpanded ? "see less" : "see more..."}
                    </Typography>
                  )}
                </Box>
              );
            })()}
          </CardContent>
        </Card>

        {/* Interview Questions Card */}
        <Card
          elevation={2}
          sx={{
            mb: 3,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0px 1px 3px rgba(0, 0, 0, 0.3)"
                : "0px 1px 3px rgba(0, 0, 0, 0.1)",
            borderRadius: 3,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "stretch", sm: "flex-start" },
                gap: { xs: 2, sm: 0 },
                mb: 3,
              }}
            >
              <Box>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{ color: theme.palette.text.primary }}
                >
                  IGNA Scout Questions
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.secondary, mt: 0.5 }}
                >
                  Questions suggested by the Hiring Manager. Review and reorder
                  as needed.
                </Typography>
              </Box>
              {/* <Button
                variant="contained"
                startIcon={<AddIcon />}
                disabled={true}
                onClick={() => handleOpenModal()}
                sx={{
                  bgcolor: "primary.main",
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                }}
              >
                Add Question
              </Button> */}
            </Box>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={
                  Array.isArray(questions?.questions)
                    ? questions?.questions.map((q) => q.id)
                    : []
                }
                strategy={verticalListSortingStrategy}
              >
                <Stack spacing={2}>
                  {questions?.questions &&
                    Array.isArray(questions?.questions) &&
                    questions?.questions?.length > 0 &&
                    questions?.questions?.map((item) => (
                      <SortableQuestion
                        key={item?.id}
                        item={item}
                        handleOpenModal={handleOpenModal}
                        reordering={reordering}
                      />
                    ))}
                </Stack>
              </SortableContext>
            </DndContext>
          </CardContent>
        </Card>
      </Box>
      <QuestionModal
        open={openModal}
        onClose={handleCloseModal}
        onSave={handleSaveQuestion}
        formData={formData}
        isEditing={!!editingQuestion}
        setFormData={setFormData}
        saving={saving}
      />
    </>
  );
};

export default InterviewQuestions;
